/**
 * Course Review Module Component
 * Displays a comprehensive survey for users to provide feedback on completed courses
 */

import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, ThumbsDown, CheckCircle, AlertCircle } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Course, CourseReviewFeedback } from '../../types/course';
import { courseService } from '../../services/firebase/courses';

interface CourseReviewModuleProps {
  course: Course;
  onSubmit: (feedback: Omit<CourseReviewFeedback, 'id' | 'courseId' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  isSubmitting?: boolean;
}

export const CourseReviewModule: React.FC<CourseReviewModuleProps> = ({
  course,
  onSubmit,
  isSubmitting = false,
}) => {
  // Required fields
  const [overallRating, setOverallRating] = useState<number>(0);
  const [contentQualityRating, setContentQualityRating] = useState<number>(0);
  const [practicalValueRating, setPracticalValueRating] = useState<number>(0);
  const [difficultyLevel, setDifficultyLevel] = useState<'too_easy' | 'just_right' | 'too_challenging' | ''>('');
  const [pace, setPace] = useState<'too_slow' | 'just_right' | 'too_fast' | ''>('');
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);

  // Optional fields
  const [whatWorkedWell, setWhatWorkedWell] = useState<string>('');
  const [whatCouldImprove, setWhatCouldImprove] = useState<string>('');
  const [skillsGained, setSkillsGained] = useState<string[]>([]);
  const [nextLearningGoal, setNextLearningGoal] = useState<string>('');

  // Validation state
  const [showValidationError, setShowValidationError] = useState(false);

  // Dynamic skills state
  const [suggestedSkills, setSuggestedSkills] = useState<string[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);

  // Load dynamic skills on mount
  useEffect(() => {
    const loadSkills = async () => {
      try {
        setIsLoadingSkills(true);
        
        // First check if course already has cached topics
        if (course.extractedTopics && course.extractedTopics.length > 0) {
          setSuggestedSkills(course.extractedTopics);
          setIsLoadingSkills(false);
          return;
        }

        // Otherwise, extract them via Cloud Function
        const topics = await courseService.extractCourseTopics(course.id);
        setSuggestedSkills(topics);
      } catch (error) {
        console.error('Error loading course topics:', error);
        // Fallback to generic skills if extraction fails
        setSuggestedSkills([
          'Understanding AI Concepts',
          'Practical Tool Usage',
          'Problem Solving',
          'Critical Thinking',
          'Technical Skills',
          'Strategic Application'
        ]);
      } finally {
        setIsLoadingSkills(false);
      }
    };

    loadSkills();
  }, [course.id, course.extractedTopics]);

  const handleSkillToggle = (skill: string) => {
    setSkillsGained((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const validateForm = (): boolean => {
    return (
      overallRating > 0 &&
      contentQualityRating > 0 &&
      practicalValueRating > 0 &&
      difficultyLevel !== '' &&
      pace !== '' &&
      wouldRecommend !== null
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setShowValidationError(true);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setShowValidationError(false);

    const feedback: Omit<CourseReviewFeedback, 'id' | 'courseId' | 'userId' | 'createdAt' | 'updatedAt'> = {
      overallRating,
      contentQualityRating,
      practicalValueRating,
      difficultyLevel: difficultyLevel as 'too_easy' | 'just_right' | 'too_challenging',
      pace: pace as 'too_slow' | 'just_right' | 'too_fast',
      wouldRecommend: wouldRecommend!,
      ...(whatWorkedWell && { whatWorkedWell }),
      ...(whatCouldImprove && { whatCouldImprove }),
      ...(skillsGained.length > 0 && { skillsGained }),
      ...(nextLearningGoal && { nextLearningGoal }),
    };

    await onSubmit(feedback);
  };

  const StarRating: React.FC<{
    rating: number;
    onChange: (rating: number) => void;
    label: string;
  }> = ({ rating, onChange, label }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-neutral-700">{label} *</label>
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 ${
                star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-300'
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-neutral-600">
          {rating > 0 ? `${rating}/5` : 'Not rated'}
        </span>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto py-8">
      <Card className="p-6 sm:p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-neutral-800 mb-2">
            Course Review & Feedback
          </h2>
          <p className="text-neutral-600">
            Your feedback helps us improve our courses and helps other learners make informed decisions.
            Please complete this review to receive your course certificate.
          </p>
        </div>

        {showValidationError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-red-800 mb-1">
                Please complete all required fields
              </h3>
              <p className="text-sm text-red-700">
                All fields marked with * are required to submit your review.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Overall Rating */}
          <div className="pb-6 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">Overall Rating</h3>
            <StarRating
              rating={overallRating}
              onChange={setOverallRating}
              label="How would you rate this course overall?"
            />
          </div>

          {/* Detailed Ratings */}
          <div className="pb-6 border-b border-neutral-200 space-y-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4">Detailed Ratings</h3>
            
            <StarRating
              rating={contentQualityRating}
              onChange={setContentQualityRating}
              label="Content Quality: How clear and comprehensive was the course material?"
            />

            <StarRating
              rating={practicalValueRating}
              onChange={setPracticalValueRating}
              label="Practical Value: How applicable is this content to real-world scenarios?"
            />
          </div>

          {/* Course Difficulty */}
          <div className="pb-6 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Course Difficulty</h3>
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              Was the course difficulty appropriate for your skill level? *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: 'too_easy', label: 'Too Easy' },
                { value: 'just_right', label: 'Just Right' },
                { value: 'too_challenging', label: 'Too Challenging' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setDifficultyLevel(option.value as any)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    difficultyLevel === option.value
                      ? 'border-primary bg-primary-50 text-primary font-semibold'
                      : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Course Pace */}
          <div className="pb-6 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Course Pace</h3>
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              How was the course pacing? *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: 'too_slow', label: 'Too Slow' },
                { value: 'just_right', label: 'Just Right' },
                { value: 'too_fast', label: 'Too Fast' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPace(option.value as any)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    pace === option.value
                      ? 'border-primary bg-primary-50 text-primary font-semibold'
                      : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Recommendation */}
          <div className="pb-6 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Recommendation</h3>
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              Would you recommend this course to others? *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setWouldRecommend(true)}
                className={`p-4 rounded-lg border-2 transition-all flex items-center justify-center gap-3 ${
                  wouldRecommend === true
                    ? 'border-green-500 bg-green-50 text-green-700 font-semibold'
                    : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                }`}
              >
                <ThumbsUp className="w-5 h-5" />
                Yes, I recommend it
              </button>
              <button
                type="button"
                onClick={() => setWouldRecommend(false)}
                className={`p-4 rounded-lg border-2 transition-all flex items-center justify-center gap-3 ${
                  wouldRecommend === false
                    ? 'border-orange-500 bg-orange-50 text-orange-700 font-semibold'
                    : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                }`}
              >
                <ThumbsDown className="w-5 h-5" />
                No, I don't recommend it
              </button>
            </div>
          </div>

          {/* Open Text Feedback */}
          <div className="pb-6 border-b border-neutral-200 space-y-4">
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Additional Feedback</h3>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                What worked well? (Optional)
              </label>
              <textarea
                value={whatWorkedWell}
                onChange={(e) => setWhatWorkedWell(e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                placeholder="Share what you found most valuable about this course..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                What could be improved? (Optional)
              </label>
              <textarea
                value={whatCouldImprove}
                onChange={(e) => setWhatCouldImprove(e.target.value)}
                className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                placeholder="Help us improve by sharing your suggestions..."
              />
            </div>
          </div>

          {/* Skills Gained */}
          <div className="pb-6 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Skills Gained</h3>
            <label className="block text-sm font-medium text-neutral-700 mb-3">
              What specific skills did you gain? (Optional - Select all that apply)
            </label>
            
            {isLoadingSkills ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-neutral-600">Loading course topics...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {suggestedSkills.map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => handleSkillToggle(skill)}
                    className={`p-3 rounded-lg border-2 transition-all text-sm ${
                      skillsGained.includes(skill)
                        ? 'border-primary bg-primary-50 text-primary font-semibold'
                        : 'border-neutral-200 hover:border-neutral-300 text-neutral-700'
                    }`}
                  >
                    {skillsGained.includes(skill) && (
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                    )}
                    {skill}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Next Learning Goal */}
          <div className="pb-6">
            <h3 className="text-lg font-semibold text-neutral-800 mb-2">Your Learning Journey</h3>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              What's your next learning goal? (Optional)
            </label>
            <input
              type="text"
              value={nextLearningGoal}
              onChange={(e) => setNextLearningGoal(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., Learn advanced prompt engineering techniques"
            />
            <p className="text-xs text-neutral-500 mt-2">
              This helps us recommend courses that align with your goals.
            </p>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting Review...' : 'Submit Review & Get Certificate'}
            </Button>
            <p className="text-xs text-neutral-500 text-center mt-3">
              After submitting, you'll be redirected to download your certificate
            </p>
          </div>
        </form>
      </Card>
    </div>
  );
};
